import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  price_30_day: "30-day plan — Stripe Price ID",
  price_60_day: "60-day plan — Stripe Price ID",
  price_90_day: "90-day plan — Stripe Price ID",
  calendly_link: "Calendly booking link",
  stripe_mode: "Stripe mode (test / live)",
};

export default function AdminSettings() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin-settings").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const setVal = (k, v) => setRows((rs) => rs.map((r) => (r.setting_key === k ? { ...r, setting_value: v } : r)));

  const save = async () => {
    setSaving(true);
    const body = {};
    rows.forEach((r) => { body[r.setting_key] = r.setting_value; });
    try {
      await api.put("/admin-settings", body);
      toast.success("Settings saved.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Admin</p>
        <h1 className="font-serif text-4xl tracking-tight mb-8">App settings</h1>
        {rows === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>
        ) : (
          <div className="space-y-5" data-testid="settings-form">
            {rows.map((r) => (
              <div key={r.setting_key} className="space-y-2">
                <Label htmlFor={r.setting_key}>{LABELS[r.setting_key] || r.setting_key}</Label>
                <Input id={r.setting_key} value={r.setting_value} onChange={(e) => setVal(r.setting_key, e.target.value)} className="bg-secondary border-border" data-testid={`setting-${r.setting_key}`} />
              </div>
            ))}
            <Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" data-testid="settings-save-btn">
              {saving && <Loader2 size={16} className="animate-spin" />} Save settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
